'use client';

import { useRef, type RefObject } from 'react';
import { LocaleDetails } from 'shared/src/types/languages.type';
import { useUiContent } from '@/components/ui-content-provider';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { offersDisclosureNotes } from './offer-copy';
import { OffersDisclosure, OffersPanel } from './offers-panel';
import { PickLine } from './pick-line';
import type { PreparedOffer } from './offers';

// The phone Back entry lives with the shared sheet now; the names stay here
// for the callers and tests that import them from the offers sheet.
export {
  attachSheetHistoryEntry,
  SHEET_HISTORY_KEY as OFFERS_SHEET_HISTORY_KEY,
} from '@/components/ui/bottom-sheet';

type OffersSheetProps = {
  open: boolean;
  onClose: () => void;
  rows: PreparedOffer[];
  /** Excluded from the rows and rendered as the ink pick line at the sheet's
   *  foot, so mobile and desktop are the same object seen twice. */
  primary: PreparedOffer;
  primaryDomainKey?: string;
  oldestObservedAt: string | null;
  localeDetails: LocaleDetails;
  productTitle: string;
  index: number;
  categoryId?: string;
  asin?: string;
  hrefFor: (offer: PreparedOffer) => string;
  expanded: boolean;
  onExpand: () => void;
  onRowClick?: (offer: PreparedOffer) => void;
  /** Forwarded to the pick line's aria-label. The sr-only disclosure and the
   *  microdata deliberately do NOT come along: the card's own pill is still in
   *  the DOM behind the scrim and there must be exactly one of each per page. */
  ctaAriaLabel?: string;
  /** Forwarded to `OffersPanel`. Always true in practice — this component only
   *  mounts once the sheet is open — but declared so the prop is passed
   *  through rather than silently dropped by the spread. */
  active?: boolean;
};

/**
 * The mobile face of the offers panel.
 *
 * A portal bottom sheet rather than the desktop morph because the product card
 * is a 100vh scroll-snap section whose CTA sits in its bottom ~8rem: anything
 * card-absolute there is at the mercy of the snap and of iOS's collapsing
 * address bar. `fixed` is immune to both.
 *
 * The shell (portal, the 60/61 stacking above the floating search button,
 * focus trap and restore, Escape, the phone Back entry) is the shared
 * `components/ui/bottom-sheet.tsx`, which the kit checkout opens too
 * (2026-09-28). The focus trap + restore is
 * `components/region/subregion-prompt-dialog.tsx`'s.
 */
export function OffersSheet({ open, onClose, ...panel }: OffersSheetProps) {
  const ui = useUiContent();
  const backButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      label={ui.chooseWhereToBuy ?? 'Where to buy'}
      scrimLabel={ui.goBack}
      initialFocusRef={backButtonRef}
      className="px-2 pb-7 pt-2"
    >
      <OffersSheetContent onClose={onClose} backButtonRef={backButtonRef} {...panel} />
    </BottomSheet>
  );
}

function OffersSheetContent({
  onClose,
  backButtonRef,
  ...panel
}: Omit<OffersSheetProps, 'open'> & { backButtonRef: RefObject<HTMLButtonElement> }) {
  const ui = useUiContent();
  return (
    <>
      <OffersPanel variant="sheet" onBack={onClose} backButtonRef={backButtonRef} {...panel} />
      <div className="px-1 pt-1">
        <PickLine
          variant="sheet"
          offer={panel.primary}
          offerIndex={panel.rows.indexOf(panel.primary)}
          localeDetails={panel.localeDetails}
          productTitle={panel.productTitle}
          index={panel.index}
          categoryId={panel.categoryId}
          asin={panel.asin}
          href={panel.hrefFor(panel.primary)}
          ctaAriaLabel={panel.ctaAriaLabel}
          onClick={() => panel.onRowClick?.(panel.primary)}
        />
      </div>
      <OffersDisclosure
        languageCode={panel.localeDetails.languageCode}
        notes={offersDisclosureNotes({
          rows: panel.rows,
          primaryDomainKey: panel.primaryDomainKey,
          productTitle: panel.productTitle,
          region: panel.localeDetails.region,
          ui,
        })}
      />
    </>
  );
}
